import React, { useState, useCallback, useEffect } from 'react';
import { formatPhone } from '../../helpers/phoneUtils';
import Grid from '@material-ui/core/Grid';
import {
    Button,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useNotify } from '../../hooks/useNotify';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import moment from 'moment';

const BookingDetails = ({ bookingInfo }) => {
    return (
        <Grid item xs={12}>
            <h2>Booking info</h2>
            <Table style={{ marginBottom: '16px' }}>
                <TableBody>
                    <TableRow>
                        <TableCell variant={'head'}>Vendor</TableCell>
                        <TableCell>{bookingInfo?.vendor}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell variant={'head'}>Activity</TableCell>
                        <TableCell>{bookingInfo?.activity}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell variant={'head'}>Start</TableCell>
                        <TableCell>{bookingInfo?.start}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <h2>Guest info</h2>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell variant={'head'}>First name</TableCell>
                        <TableCell>{bookingInfo?.guest?.firstname || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell variant={'head'}>Last name</TableCell>
                        <TableCell>{bookingInfo?.guest?.lastname || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell variant={'head'}>Email</TableCell>
                        <TableCell>{bookingInfo?.guest?.email || '-'}</TableCell>
                    </TableRow>
                    {bookingInfo?.guest?.additional_emails.length > 0 && (
                        <TableRow>
                            <TableCell variant={'head'}>Additional emails</TableCell>
                            <TableCell>
                                {bookingInfo?.guest?.additional_emails.map(email => (
                                    <div key={email}>{email}</div>
                                ))}
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell variant={'head'}>Phone</TableCell>
                        <TableCell>{formatPhone(bookingInfo?.guest?.phone) || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell variant={'head'}>Notifications</TableCell>
                        <TableCell>
                            {bookingInfo?.guest?.notifications.map(notification => (
                                <div key={notification}>{notification}</div>
                            )) || '-'}
                        </TableCell>
                    </TableRow>
                    {bookingInfo?.guest?.arrival_date && (
                        <TableRow>
                            <TableCell variant={'head'}>Arrival date</TableCell>
                            <TableCell>
                                {moment
                                    .utc(bookingInfo?.guest?.arrival_date)
                                    .format('YYYY-MM-DD') || '-'}
                            </TableCell>
                        </TableRow>
                    )}
                    {bookingInfo?.guest?.departure_date && (
                        <TableRow>
                            <TableCell variant={'head'}>Departure date</TableCell>
                            <TableCell>
                                {moment
                                    .utc(bookingInfo?.guest?.departure_date)
                                    .format('YYYY-MM-DD') || '-'}
                            </TableCell>
                        </TableRow>
                    )}
                    {bookingInfo?.guest?.guest_field_info &&
                        bookingInfo?.guest?.guest_field_info.length > 0 &&
                        bookingInfo?.guest?.guest_field_info.map(field => {
                            let name = field.name;
                            let value = field.value;
                            if (field.hotel) {
                                name = 'Hotel';
                                value = `${field.hotel.name}, ${field.hotel.island}` || '-';
                            }
                            if (typeof value === 'boolean') {
                                value = value ? 'yes' : 'no';
                            }
                            return (
                                <TableRow key={field.id}>
                                    <TableCell variant={'head'}>{name}</TableCell>
                                    <TableCell>{value}</TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
            </Table>
        </Grid>
    );
};

const ConfirmOrDenyBooking = ({ pageType, id, hash }) => {
    const { notifySuccess, notifyError } = useNotify();
    const [confirmationFields, setConfirmationFields] = useState({
        bookingId: '',
        confirmationCode: '',
        message: ''
    });
    const [bookingInfo, setBookingInfo] = useState({});
    const [loading, setLoading] = useState(false);
    // bookingState is the result of the api call, false if no such call is made
    const [bookingState, setBookingState] = useState(false);

    useEffect(() => {
        doSwaggerCall('bookings', 'getVendorCodeByHash', {
            bookingItemId: id,
            bookingHash: hash
        })
            .then(res => {
                setConfirmationFields(prevValues => ({
                    ...prevValues,
                    confirmationCode: res?.vendorCode || ''
                }));
                setBookingInfo(res);
            })
            .catch(err => {
                notifyError(err.response.body.message);
            });
    }, [setConfirmationFields, hash, id, notifyError]);

    const confirmOrDenyBooking = useCallback(() => {
        setLoading(true);
        doSwaggerCall('bookings', pageType === 'confirm' ? 'confirmBooking' : 'denyBooking', {
            bookingItemId: id,
            hash,
            ...(pageType === 'confirm'
                ? {
                      confirmation: {
                          bookingId: confirmationFields.bookingId,
                          confirmationCode: confirmationFields.confirmationCode,
                          confirmationMessage: confirmationFields.message
                      }
                  }
                : {
                      denial: {
                          bookingId: confirmationFields.bookingId,
                          confirmationCode: confirmationFields.confirmationCode,
                          denyMessage: confirmationFields.message
                      }
                  })
        })
            .then(result => {
                setBookingState(result);
                notifySuccess('Booking confirmed successfully');
            })
            .catch(error => {
                notifyError(error.response.body.message);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [confirmationFields, hash, id, notifyError, notifySuccess, pageType]);

    if (loading) {
        return (
            <>
                <Grid item xs={12}>
                    {pageType === 'confirm' && <h1>Confirmation in progress...</h1>}
                    {pageType === 'deny' && <h1>Deny reservation in progress...</h1>}
                </Grid>
                <Grid item xs={12}>
                    <LinearProgress />
                </Grid>
            </>
        );
    }

    // confirmation form
    if (bookingState === false && pageType === 'confirm') {
        return (
            <>
                <Grid item xs={12}>
                    <h2>Confirmation</h2>
                    <p>
                        During the confirmation, you can set a Booking ID and Confirmation Code to
                        make the guest verification process easier. These fields are optional.
                    </p>
                </Grid>
                <BookingDetails bookingInfo={bookingInfo} />
                <Grid item xs={12}>
                    <h2>Response</h2>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Booking ID"
                        fullWidth
                        type="text"
                        value={confirmationFields.bookingId}
                        onChange={ev =>
                            setConfirmationFields({
                                ...confirmationFields,
                                bookingId: ev.target.value
                            })
                        }
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Confirmation Code"
                        fullWidth
                        type="text"
                        value={confirmationFields.confirmationCode}
                        onChange={ev =>
                            setConfirmationFields({
                                ...confirmationFields,
                                confirmationCode: ev.target.value
                            })
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Vendor note to guest"
                        fullWidth
                        type="text"
                        value={confirmationFields.message}
                        onChange={ev =>
                            setConfirmationFields({
                                ...confirmationFields,
                                message: ev.target.value
                            })
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={confirmOrDenyBooking}
                        fullWidth
                    >
                        Confirm
                    </Button>
                </Grid>
            </>
        );
    }

    // deny form
    if (bookingState === false && pageType === 'deny') {
        return (
            <>
                <Grid item xs={12}>
                    <h2>Deny reservation</h2>
                    <p>
                        During the deny of a reservation, you can add a message to notify the guest
                        why the booking is denied. This field is optional.
                    </p>
                </Grid>
                <BookingDetails bookingInfo={bookingInfo} />
                <Grid item xs={12}>
                    <h2>Response</h2>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Deny message"
                        fullWidth
                        type="text"
                        value={confirmationFields.message}
                        onChange={ev =>
                            setConfirmationFields({
                                ...confirmationFields,
                                message: ev.target.value
                            })
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={confirmOrDenyBooking}
                        fullWidth
                    >
                        Deny
                    </Button>
                </Grid>
            </>
        );
    }

    // we should have bookingState ready by now (not loading, not confirmation form) but let's check
    if (bookingState === false) {
        return null;
    }

    return (
        <>
            <Grid item xs={12}>
                <h2>{bookingState.state === 'error' ? 'Error!' : 'Success!'}</h2>
            </Grid>
            <Grid item xs={12}>
                <Alert severity={bookingState.state === 'error' ? 'error' : 'success'}>
                    {bookingState.message}
                </Alert>
            </Grid>
        </>
    );
};

export default ConfirmOrDenyBooking;
